import apiClient, { setAuthToken } from "./apiClient";
import { UserType } from "@/utils/userTypes.ts";
import { FamilyType } from "@/features/Login/Family/types.ts";

const AUTH_ROUTES = {
  LOGIN: "/users/login",
  REGISTER: "/users/register",
  GOOGLE_AUTH: "/users/auth/google",
  USER_PROFILE: (id: string) => `/users/${id}`,
  FAMILY_DETAILS: "/users/family/details",
  JOIN_FAMILY: "/users/joinFamily",
  LEAVE_FAMILY: "/users/leaveFamily",
  CREATE_FAMILY: "/users/createFamily",
  REGENERATE_INVITE: "/users/family/regenerateInviteCode",
  ADD_HOUSE: "/users/house",
  REMOVE_HOUSE: (id: string) => `/users/house/${id}`,
};

// Authentification
export const login = async (credentials: {
  login: string;
  password: string;
}) => {
  const response = await apiClient.post(AUTH_ROUTES.LOGIN, credentials);
  const token = response.data;
  setAuthToken(token);
  return token;
};

export const registerUser = async (userData: FormData) => {
  const response = await apiClient.post(AUTH_ROUTES.REGISTER, userData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const token = response.data;
  setAuthToken(token);
  return token;
};

export const googleAuth = async (credential: string) => {
  const response = await apiClient.post(AUTH_ROUTES.GOOGLE_AUTH, {
    credential,
  });
  const token = response.data;
  setAuthToken(token);
  return token;
};

export const logout = () => {
  setAuthToken(null);
};

// Gestion du profil
export const getUserProfile = async (userId: string): Promise<UserType> => {
  const response = await apiClient.get(AUTH_ROUTES.USER_PROFILE(userId));
  return response.data;
};

export const updateUserProfile = async (userId: string, userData: FormData) => {
  const response = await apiClient.patch(
    AUTH_ROUTES.USER_PROFILE(userId),
    userData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  // Si un nouveau token est renvoyé, mise à jour
  if (response.data?.token) {
    setAuthToken(response.data.token);
    return { token: response.data.token, user: response.data.user };
  }

  return response.data;
};

// Gestion des familles
export const getFamilyDetails = async (): Promise<FamilyType> => {
  const response = await apiClient.get(AUTH_ROUTES.FAMILY_DETAILS);
  return response.data;
};

export const createFamily = async (data: { name: string; owner: string }) => {
  const response = await apiClient.post(AUTH_ROUTES.CREATE_FAMILY, data);
  return response.data;
};

export const joinFamily = async (inviteCode: string) => {
  const response = await apiClient.post(AUTH_ROUTES.JOIN_FAMILY, {
    inviteCode,
  });
  return response.data;
};

export const leaveFamily = async () => {
  const response = await apiClient.post(AUTH_ROUTES.LEAVE_FAMILY, {});
  return response.data;
};

export const regenerateInviteCode = async () => {
  const response = await apiClient.post(AUTH_ROUTES.REGENERATE_INVITE, {});
  return response.data;
};

export const addHouse = async (houseData: {
  name: string;
  address?: string;
}) => {
  const response = await apiClient.post(AUTH_ROUTES.ADD_HOUSE, houseData);
  return response.data;
};

export const removeHouse = async (houseId: string) => {
  const response = await apiClient.delete(AUTH_ROUTES.REMOVE_HOUSE(houseId));
  return response.data;
};
